<script setup lang="ts">
import {
	computed,
	onBeforeUnmount,
	onMounted,
	ref
} from "vue"

type Props = {
	seconds?: number
	size?: number
	color?: string
}

const props = withDefaults(defineProps<Props>(), {
	seconds: 5,
	size: 32,
	color: '#979797'
})

const emit = defineEmits<{
	(e: 'finish'): void
}>()

const countdown = ref(props.seconds)
let tickTimer: ReturnType<typeof setInterval> | null = null
let finishTimer: ReturnType<typeof setTimeout> | null = null
let finished = false

const rootStyle = computed(() => ({
	'--cdt-duration': `${props.seconds}s`,
	'--cdt-size': `${props.size}px`,
	'--cdt-font-size': `${Math.round(props.size * 0.4)}px`,
	'--cdt-color': props.color
}))

const clearTimers = () => {
	if (tickTimer) {
		clearInterval(tickTimer)
		tickTimer = null
	}
	if (finishTimer) {
		clearTimeout(finishTimer)
		finishTimer = null
	}
}

const finish = () => {
	if (finished) {
		return
	}
	finished = true
	clearTimers()
	emit('finish')
}

onMounted(() => {
	tickTimer = setInterval(() => {
		countdown.value = Math.max(0, countdown.value - 1)
	}, 1000)
	finishTimer = setTimeout(finish, props.seconds * 1000)
})

onBeforeUnmount(clearTimers)

defineExpose({ flush: finish })
</script>

<template>
	<div class="countdown-timer" :style="rootStyle">
		<svg class="countdown-timer__svg" viewBox="0 0 20 20">
			<circle class="countdown-timer__circle" cx="10" cy="10" r="9" />
		</svg>
		<span class="countdown-timer__num">{{ countdown }}</span>
	</div>
</template>

<style scoped lang="scss">
.countdown-timer {
	position: relative;
	width: var(--cdt-size, 32px);
	height: var(--cdt-size, 32px);
	flex-shrink: 0;

	&__svg {
		width: 100%;
		height: 100%;
		transform: rotate(90deg) scaleX(-1);
	}

	&__circle {
		fill: none;
		stroke: var(--cdt-color, #979797);
		stroke-width: 2;
		stroke-dasharray: 56.549;
		stroke-dashoffset: 0;
		animation: cdt-deplete var(--cdt-duration, 5s) linear forwards;
	}

	&__num {
		position: absolute;
		inset: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: var(--cdt-font-size, 13px);
		line-height: 1;
		font-weight: 600;
		color: var(--cdt-color, #979797);
	}
}

@keyframes cdt-deplete {
	from { stroke-dashoffset: 0; }
	to { stroke-dashoffset: 56.549; }
}
</style>
